import { ComboboxItem } from '@mantine/core';
import i18n, { DEFAULT_LANG } from '@vision/ui/i18n';
import dayjs from 'dayjs';
import { VisionDateFormatType, VisionDateFormatTypeValue, VisionTimeFormatType } from '../enums';
import { DateFormatVariant } from '../interfaces';
import { getCookieLocalizationSettings } from './storage.utils';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(duration);
dayjs.extend(relativeTime);

export function getParsedDate(date: number | string | Date) {
  if (date instanceof Date) {
    return date;
  }

  return new Date(date);
}

function getDateIntlLanguage(variant: DateFormatVariant, locale?: string) {
  // Date format lang should always be relative to "en" if variant is "full"
  if (variant === 'full') {
    return DEFAULT_LANG;
  }

  return locale || i18n.language;
}

function getTimeIntlLanguage(locale: string) {
  return locale || i18n.language;
}

function createDateIntlInstance(variant: DateFormatVariant, locale?: string) {
  const lang = getDateIntlLanguage(variant, locale);

  const options: Intl.DateTimeFormatOptions = {
    month: variant === 'medium' || variant === 'monthYear' ? 'long' : '2-digit',
    day: '2-digit',
  };

  if (variant === 'full' || variant === 'monthYear' || variant === 'year') {
    options.year = 'numeric';
  }

  return new Intl.DateTimeFormat(lang, options);
}

function createTimeIntlInstance(locale: string, hour12?: boolean) {
  const lang = getTimeIntlLanguage(locale);

  return new Intl.DateTimeFormat(lang, {
    hour: 'numeric',
    minute: 'numeric',
    ...(hour12 !== undefined ? { hour12 } : {}),
  });
}

function formatDateByMonthYearVariant(date: number | string | Date, locale?: string) {
  const dateIntl = createDateIntlInstance('monthYear', locale);
  const isH12 = getCookieLocalizationTimeFormat() === VisionTimeFormatType.H12;
  const timeIntl = createTimeIntlInstance(locale, isH12);
  const parsedDate = getParsedDate(date);

  const { month, year } = dateIntl
    .formatToParts(parsedDate)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {} as Record<Intl.DateTimeFormatPartTypes, string>);

  return {
    date: `${month} ${year}`,
    time: timeIntl.format(parsedDate),
  };
}

function formatDateByYearVariant(date: number | string | Date, locale?: string) {
  const dateIntl = createDateIntlInstance('year', locale);
  const isH12 = getCookieLocalizationTimeFormat() === VisionTimeFormatType.H12;
  const timeIntl = createTimeIntlInstance(locale, isH12);
  const parsedDate = getParsedDate(date);

  const { year } = dateIntl
    .formatToParts(parsedDate)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {} as Record<Intl.DateTimeFormatPartTypes, string>);

  return {
    date: year,
    time: timeIntl.format(parsedDate),
  };
}

function formatDateByQuarterVariant(date: number | string | Date, locale?: string) {
  const dateIntl = createDateIntlInstance('year', locale);
  const isH12 = getCookieLocalizationTimeFormat() === VisionTimeFormatType.H12;
  const timeIntl = createTimeIntlInstance(locale, isH12);
  const parsedDate = getParsedDate(date);

  const { year } = dateIntl
    .formatToParts(parsedDate)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {} as Record<Intl.DateTimeFormatPartTypes, string>);

  const quarter = Math.ceil((parsedDate.getMonth() + 1) / 3);

  return {
    date: `${i18n.t('translation:shortQuarter')}${quarter} ${year}`,
    time: timeIntl.format(parsedDate),
  };
}

function formatDateByMediumVariant(date: number | string | Date, locale?: string) {
  const dateIntl = createDateIntlInstance('medium', locale);
  const isH12 = getCookieLocalizationTimeFormat() === VisionTimeFormatType.H12;
  const timeIntl = createTimeIntlInstance(locale, isH12);
  const parsedDate = getParsedDate(date);

  return {
    date: dateIntl.format(parsedDate),
    time: timeIntl.format(parsedDate),
  };
}

function formatDateByShortMonthDayVariant(date: number | string | Date, locale?: string) {
  const lang = locale || i18n.language;
  const isH12 = getCookieLocalizationTimeFormat() === VisionTimeFormatType.H12;
  const dateIntl = new Intl.DateTimeFormat(lang, {
    month: 'short',
    day: '2-digit',
  });
  const timeIntl = createTimeIntlInstance(locale, isH12);
  const parsedDate = getParsedDate(date);

  return {
    date: dateIntl.format(parsedDate),
    time: timeIntl.format(parsedDate),
  };
}

export function getTimeListByInterval(interval = 60) {
  const timeList: string[] = [];

  let startTime = dayjs().startOf('day');
  const endTime = dayjs().endOf('day');

  while (startTime.isBefore(endTime)) {
    timeList.push(startTime.format('HH:mm'));
    startTime = startTime.add(interval, 'minute');
  }

  return timeList;
}

export function getTimeListByIntervalSelectItems(interval = 60): ComboboxItem[] {
  const timeList: ComboboxItem[] = [];

  let startTime = dayjs().startOf('day');
  const endTime = dayjs().endOf('day');

  while (startTime.isBefore(endTime)) {
    timeList.push({
      label: startTime.format(getCookieLocalizationTimeFormatString()),
      value: startTime.format('HH:mm'),
    });
    startTime = startTime.add(interval, 'minute');
  }

  return timeList;
}

export function to12HourFormat(time: string) {
  const [hour, minute] = time.split(':').map(Number);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${ampm}`;
}

export function formatTime(time: string) {
  const format = getCookieLocalizationTimeFormatString();
  return format === 'hh:mm A' ? to12HourFormat(time) : time;
}

export function getCookieLocalizationDateFormat(): string {
  const localizationSettings = getCookieLocalizationSettings();
  return localizationSettings?.date_format === VisionDateFormatType.US
    ? VisionDateFormatTypeValue.US
    : VisionDateFormatTypeValue.EU;
}

export function getCookieLocalizationTimeFormat(): VisionTimeFormatType {
  const localizationSettings = getCookieLocalizationSettings();
  return localizationSettings?.time_format;
}

export function getCookieLocalizationTimeFormatString(): 'hh:mm A' | 'HH:mm' {
  const localizationSettings = getCookieLocalizationSettings();
  return localizationSettings?.time_format === VisionTimeFormatType.H12 ? 'hh:mm A' : 'HH:mm';
}

function formatDateByFullVariant(date: number | string | Date, locale?: string) {
  const dateIntl = createDateIntlInstance('full', locale);
  const localizationSettings = getCookieLocalizationSettings();
  const isUS = localizationSettings?.date_format === VisionDateFormatType.US;
  const isH12 = getCookieLocalizationTimeFormat() === VisionTimeFormatType.H12;
  const timeIntl = createTimeIntlInstance(locale, isH12);
  const parsedDate = getParsedDate(date);
  const { day, month, year } = dateIntl
    .formatToParts(parsedDate)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {} as Record<Intl.DateTimeFormatPartTypes, string>);

  if (isUS) {
    return {
      date: `${month}/${day}/${year}`,
      time: timeIntl.format(parsedDate),
    };
  }

  return {
    date: `${day}/${month}/${year}`,
    time: timeIntl.format(parsedDate),
  };
}

export function formatDate(date: number | string | Date, variant: DateFormatVariant = 'full', locale = DEFAULT_LANG) {
  if (!date) {
    return;
  }

  if (variant === 'medium') {
    return formatDateByMediumVariant(date, locale);
  } else if (variant === 'monthYear') {
    return formatDateByMonthYearVariant(date, locale);
  } else if (variant === 'year') {
    return formatDateByYearVariant(date, locale);
  } else if (variant === 'quarter') {
    return formatDateByQuarterVariant(date, locale);
  }

  return formatDateByFullVariant(date, locale);
}

export function formatDateWithTemplate(date: number | string | Date, template: string) {
  return dayjs(date).format(template);
}

// Day and long month with locale-correct ordering (e.g. "June 1" vs "1 Haziran"),
// driven by Intl so every supported language is handled, not just TR vs EN.
export function formatDayMonth(date: number | string | Date, locale?: string) {
  return new Intl.DateTimeFormat(locale || i18n.language, {
    day: 'numeric',
    month: 'long',
  }).format(dayjs(date).toDate());
}

export function isSameDate(date: Date | null, value?: string | null) {
  const valueDate = value ? getParsedDate(value) : null;

  if (!date && !valueDate) {
    return true;
  }

  if (!date || !valueDate) {
    return false;
  }

  return date.getTime() === valueDate.getTime();
}

export function formatDateRangeShort(
  fromDate: number | string | Date,
  toDate: number | string | Date,
  locale = DEFAULT_LANG,
) {
  const formattedFromDate = formatDateByShortMonthDayVariant(fromDate, locale).date;
  const formattedToDate = formatDateByShortMonthDayVariant(toDate, locale).date;

  return `${formattedFromDate} - ${formattedToDate}`;
}

export function getThirtyDaysAgo(): Date {
  return dayjs().subtract(30, 'day').toDate();
}

export function createHourOptions(): ComboboxItem[] {
  return Array.from({ length: 24 }).map((_, hour) => {
    const hh = String(hour).padStart(2, '0') + ':00';
    return { label: hh, value: hh };
  });
}

export function generateHourOptions(): ComboboxItem[] {
  const hours = Array.from(Array(24).keys());
  return hours.map((hour) => ({
    label: `${hour.toString().padStart(2, '0')}:00`,
    value: hour.toString(),
  }));
}

export function generateWeekdayOptions(): ComboboxItem[] {
  return dayjs
    .localeData()
    .weekdays()
    .map((day, index) => ({
      value: index.toString(),
      label: day,
    }));
}

export function generateDayOptions(): ComboboxItem[] {
  return Array.from({ length: 31 }, (_, day) => {
    const dayValue = (day + 1).toString();
    return {
      value: dayValue,
      label: dayValue,
    };
  });
}

export function calculateDayDiff(date: number | string | Date): number {
  const completed = getParsedDate(date).getTime();
  const now = Date.now();
  return (now - completed) / (1000 * 60 * 60 * 24);
}

export const formatTimestamp = (timestamp: number, variant: DateFormatVariant = 'full', locale?: string) => {
  return formatDate(timestamp * 1000, variant, locale);
};

export const formatDuration = (seconds: number) => {
  return dayjs.duration(seconds, 'seconds').humanize();
};
